import React from 'react';
import { Plus, Trash2, Sparkles } from 'lucide-react';
import ColorEditor from '../ColorEditor';

const FAQEditor = ({ content, onContentChange, onArrayFieldChange, onAddArrayItem, onRemoveArrayItem, onSectionRegenerate, regeneratingSection, site, onCreateCustomTheme }) => {
  // Initialize FAQ items if empty
  React.useEffect(() => {
    if (!content.faqs || content.faqs.length === 0) {
      onContentChange('faqs', [
        { question: 'What services do you offer?', answer: 'We offer comprehensive services tailored to meet your specific needs and exceed your expectations.' },
        { question: 'How long have you been in business?', answer: 'We have been serving our community with dedication and expertise for several years.' },
        { question: 'What makes you different from competitors?', answer: 'Our commitment to quality, personalized service, and customer satisfaction sets us apart from the competition.' }
      ]);
    }
  }, []);


  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Frequently Asked Questions"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Description
        </label>
        <textarea
          value={content.description || ''}
          onChange={(e) => onContentChange('description', e.target.value)}
          className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Find answers to common questions about our services and offerings."
          rows="4"
        />
      </div>

      {/* FAQ Items */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">
            FAQ Items
          </label>
          <div className="flex items-center space-x-2">
            {onSectionRegenerate && (
              <button
                onClick={onSectionRegenerate}
                disabled={regeneratingSection}
                className="flex items-center space-x-1 px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
              >
                <Sparkles className="w-4 h-4" />
                <span>{regeneratingSection ? 'Generating...' : 'AI Generate'}</span>
              </button>
            )}
            <button
              onClick={() => onAddArrayItem('faqs', { question: '', answer: '' })}
              className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add FAQ</span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {content.faqs?.map((faq, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-1 bg-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  FAQ {index + 1}
                </span>
                <button
                  onClick={() => onRemoveArrayItem('faqs', index)}
                  className="p-1 hover:bg-red-100 rounded text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question
                  </label>
                  <input
                    type="text"
                    value={faq.question || ''}
                    onChange={(e) => onArrayFieldChange('faqs', index, 'question', e.target.value)}
                    className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your question..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Answer
                  </label>
                  <textarea
                    rows={6}
                    value={faq.answer || ''}
                    onChange={(e) => onArrayFieldChange('faqs', index, 'answer', e.target.value)}
                    className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Provide a detailed answer..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Display Settings */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Display Settings
        </label>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Show section heading</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                type="checkbox" 
                checked={content.showHeading !== false}
                onChange={(e) => onContentChange('showHeading', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Expand first FAQ by default</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                type="checkbox" 
                checked={content.expandFirst !== false}
                onChange={(e) => onContentChange('expandFirst', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
        </div>
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default FAQEditor;